import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowDownRight, Check } from "lucide-react";
import { MaskLines } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/SectionHead";
import { IMAGE_ALT } from "@/data/site";

const FLOW = ["Website", "CRM & leads", "Bookings", "Memberships", "Payments"];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.04, 1.14]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 110, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 110, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <section ref={ref} className="relative overflow-hidden bg-paper pt-28 md:pt-36 pb-16 md:pb-24" data-testid="hero-section">
      <svg className="pointer-events-none absolute -left-40 bottom-0 h-[520px] w-[900px] text-sand" viewBox="0 0 900 520" fill="none" aria-hidden>
        {[0, 1, 2, 3, 4].map((i) => (
          <path key={i} d={`M${i * 70} 520 L${300 + i * 70} 40 L${600 + i * 70} 520`} stroke="currentColor" strokeWidth="2" />
        ))}
      </svg>
      <div className="container-x relative grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-10">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <Eyebrow className="!tracking-[0.18em]">Digital Solutions for Fitness, Sports & Wellness</Eyebrow>
          </motion.div>
          <MaskLines
            testId="hero-heading"
            className="mt-7 text-[2.65rem] sm:text-6xl lg:text-[4.4rem] xl:text-[5rem] font-extrabold leading-[0.98] tracking-[-0.035em] text-ink"
            lines={[
              "Grow Your Fitness",
              "Business with",
              <span key="s">
                <span className="relative inline-block">
                  <span className="relative z-10">Smarter</span>
                  <motion.span className="absolute inset-x-[-0.06em] bottom-[0.1em] z-0 h-[0.32em] origin-left bg-lime" initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 1.1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }} aria-hidden />
                </span>{" "}
                Digital
              </span>,
              "Solutions",
            ]}
          />
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8 }} className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-ink/75">
            Athlevo IT Solution helps gyms, fitness studios, sports academies, wellness centres, and membership-based businesses grow through modern websites, custom software, CRM, membership systems, booking platforms, mobile applications, and digital marketing.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }} className="mt-10 flex flex-col gap-3 sm:flex-row">
            <Link to="/contact#inquiry" className="btn-lime" data-testid="hero-primary-cta">Discuss Your Project <ArrowRight className="h-4 w-4" aria-hidden /></Link>
            <Link to="/services" className="btn-ghost" data-testid="hero-secondary-cta">Explore Our Services</Link>
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.15, duration: 0.8 }} className="mt-8 flex items-start gap-2 text-sm font-medium text-ink/70">
            <ArrowDownRight className="mt-0.5 h-4 w-4 shrink-0 text-sageDeep" aria-hidden />
            Built for fitness businesses. Designed for better customer experiences and sustainable growth.
          </motion.p>
        </motion.div>

        <div className="lg:col-span-5 [perspective:1200px]" onMouseMove={onMove} onMouseLeave={onLeave}>
          <motion.div
            style={{ rotateX: rx, rotateY: ry }}
            initial={{ scale: 0.96 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative [transform-style:preserve-3d]"
          >
            <motion.div initial={{ clipPath: "inset(14% 14% 14% 14%)" }} animate={{ clipPath: "inset(0% 0% 0% 0%)" }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
            <div className="clip-hero relative aspect-[4/3] overflow-hidden bg-ink sm:aspect-[5/4] lg:aspect-[4/5]">
              <motion.img
                style={{ y: imgY, scale: imgScale }}
                src="/images/hero.webp"
                alt={IMAGE_ALT.hero}
                width="1600"
                height="1066"
                fetchPriority="high"
                className="absolute inset-0 h-full w-full object-cover object-[60%_center]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/50 to-transparent" aria-hidden />
            </div>
            </motion.div>
            <div className="relative mt-4 rounded-xl bg-ink p-5 shadow-2xl on-dark sm:absolute sm:-bottom-6 sm:right-[-12px] sm:mt-0 sm:w-64 [transform:translateZ(60px)]" data-testid="hero-flow-card">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-lime">One connected system</p>
              <ul className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-1">
                {FLOW.map((f, i) => (
                  <motion.li key={f} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.2 + i * 0.1 }} className="flex items-center gap-2 text-sm text-white">
                    <Check className="h-3.5 w-3.5 text-lime" aria-hidden />{f}
                  </motion.li>
                ))}
              </ul>
            </div>
            <span className="absolute -left-3 top-10 hidden h-24 w-1.5 bg-coral lg:block" aria-hidden />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
